import { GroupBase } from 'react-select';

import { ChartType } from 'types/dashboard_widget';

import { Icon } from 'components/Icon/Icon';
import { Select } from 'components/Select/Select';

type ChartSelectProps = {
  value?: ChartType;
  onChange?: (value: ChartType) => void;
};

type Option = {
  label: string;
  value: ChartType;
  description?: string;
};

const groupedOptions: GroupBase<Option>[] = [
  {
    label: 'Time series',
    options: [
      {
        value: 'timeseries-bar' as const,
        label: 'Bar',
        description: 'Visualize values over time using vertical bars.',
      },
      {
        value: 'timeseries-line' as const,
        label: 'Line',
        description: 'Visualize values over time using a continuous line.',
      },
      {
        value: 'timeseries-bar-stacked' as const,
        label: 'Stacked',
        description:
          'Visualize values and their composition over time using vertically stacked bars.',
      },
      {
        value: 'timeseries-bar-grouped' as const,
        label: 'Grouped',
        description:
          'Visualize values and their composition over time using vertical bars side by side.',
      },
    ],
  },
  {
    label: 'Rankings and proportions',
    options: [
      {
        value: 'leaderboard-bar' as const,
        label: 'Leaderboard Bar',
        description:
          'Compare values from highest to lowest using horizontal bars.',
      },
      {
        value: 'leaderboard-table' as const,
        label: 'Leaderboard Table',
        description: 'Compare values from highest to lowest listed in a table.',
      },
      {
        value: 'pie' as const,
        label: 'Pie',
        description: 'Compare proportions visualized in a circular graph.',
      },
    ],
  },
  {
    label: 'Others',
    options: [
      {
        value: 'counter' as const,
        label: 'Number',
        description: 'Measure values with a single number.',
      },
      {
        value: 'table' as const,
        label: 'Table',
        description: 'List raw data.',
      },
      {
        value: 'timeseries-heatmap' as const,
        label: 'Heatmap',
        description: 'Visualize patterns and trends using color intensity.',
      },
    ],
  },
];

export function ChartSelect({ value, onChange }: ChartSelectProps) {
  const selectedOption = groupedOptions
    .flatMap((group) => group.options)
    .find((option) => option.value === value);

  return (
    <Select
      value={selectedOption}
      onChange={(option) => {
        if (!option) {
          return;
        }

        onChange?.(option.value);
      }}
      options={groupedOptions}
      placeholder="Select display type..."
      formatOptionLabel={({ value, label, description }, { context }) => (
        <div className="flex items-center">
          <Icon name={value} size={context === 'menu' ? 'large' : 'medium'} />

          {context === 'menu' ? (
            <div>
              <strong className="text-sm">{label}</strong>
              <br />
              {description && <span className="text-xs">{description}</span>}
            </div>
          ) : (
            <span>{label}</span>
          )}
        </div>
      )}
    />
  );
}
